import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ShoppingCart, ArrowLeft, ShieldCheck, Truck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImage } from "@/lib/zafaraApi";
import { useShop } from "@/context/ShopContext";
import { AppPromoCard } from "@/components/shop/AppPromoCard";
import { ProductImage } from "@/components/shop/ProductImage";

const formatXOF = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Number(n) || 0) + " F";

export default function ProduitDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, addToCart } = useShop();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gallery, setGallery] = useState(0);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const p = await api.product(id);
        const prod = p?.product || p;
        // Normalize sizes: API may return string[] OR [{size, stock}, ...]
        if (Array.isArray(prod?.sizes)) {
          prod.sizes = prod.sizes.map((s) =>
            typeof s === "string" ? { size: s, stock: null } : s
          );
        }
        setProduct(prod);
        if (prod?.sizes?.length) setSize(prod.sizes[0].size);
      } catch (e) {
        setError(e.message || "Produit introuvable.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handleAdd = async () => {
    if (!user) {
      toast.info("Connectez-vous pour ajouter au panier.");
      navigate(`/compte/connexion?next=/produit/${id}`);
      return;
    }
    setBusy(true);
    try {
      await addToCart(product.id, qty, size);
      toast.success("Ajouté au panier.");
    } catch (e) {
      toast.error(e.message || "Ajout impossible.");
    } finally { setBusy(false); }
  };

  if (loading) return (
    <div className="mx-auto max-w-7xl px-5 py-24 flex items-center justify-center text-[#16241B]/60">
      <Loader2 className="h-6 w-6 animate-spin mr-3" /> Chargement…
    </div>
  );
  if (error || !product) return (
    <div className="mx-auto max-w-3xl px-5 py-16 text-center">
      <p className="text-[#E8112D]">{error || "Produit introuvable."}</p>
      <Link to="/catalogue" className="mt-6 inline-flex items-center gap-2 text-[#0E7A3B] hover:underline">
        <ArrowLeft className="h-4 w-4" /> Retour au catalogue
      </Link>
    </div>
  );

  const images = product.images?.length ? product.images : (product.image_url ? [product.image_url] : []);
  const stock = product.stock ?? 0;
  const out = stock <= 0;
  const productUrl = `https://zafarabenin.fr/produit/${product.id}`;
  const primaryImage = images[0] ? resolveImage(images[0]) : "https://zafarabenin.fr/og-image.png";

  return (
    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-8 md:py-14">
      <Helmet>
        <title>{`${product.name} — Zafara Bénin`}</title>
        <meta
          name="description"
          content={(product.description || `${product.name} — livré partout au Bénin. Paiement Mobile Money.`).slice(0, 160)}
        />
        <link rel="canonical" href={productUrl} />
        <meta name="robots" content="index,follow" />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${product.name} — Zafara Bénin`} />
        <meta property="og:description" content={(product.description || "").slice(0, 200)} />
        <meta property="og:url" content={productUrl} />
        <meta property="og:image" content={primaryImage} />
        <meta property="product:price:amount" content={String(product.price || "")} />
        <meta property="product:price:currency" content="XOF" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.name,
            description: product.description || product.name,
            image: images.map((i) => resolveImage(i)),
            sku: product.sku || product.id,
            category: product.category || undefined,
            brand: product.brand ? { "@type": "Brand", name: product.brand } : { "@type": "Brand", name: "Zafara Bénin" },
            offers: {
              "@type": "Offer",
              url: productUrl,
              priceCurrency: "XOF",
              price: product.price,
              availability: out
                ? "https://schema.org/OutOfStock"
                : "https://schema.org/InStock",
              itemCondition: "https://schema.org/NewCondition",
              seller: { "@type": "Organization", name: "Zafara Bénin" },
            },
          })}
        </script>
      </Helmet>
      <Link to="/catalogue" className="inline-flex items-center gap-2 text-sm text-[#16241B]/70 hover:text-[#0E7A3B] mb-6">
        <ArrowLeft className="h-4 w-4" /> Retour au catalogue
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
        <div>
          <div className="aspect-square rounded-3xl overflow-hidden bg-white border border-[#EDE7D9]">
            <ProductImage
              src={images[gallery]}
              alt={product.name}
              className="h-full w-full object-cover"
            />
          </div>
          {images.length > 1 && (
            <div className="mt-4 flex gap-2 overflow-x-auto">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setGallery(i)}
                  className={`shrink-0 h-20 w-20 rounded-xl overflow-hidden border-2 transition-colors ${
                    gallery === i ? "border-[#0E7A3B]" : "border-[#EDE7D9]"
                  }`}
                >
                  <ProductImage src={img} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="font-utility text-xs uppercase tracking-[0.25em] text-[#0E7A3B]">{product.category || "Produit"}</p>
          <h1 data-testid="product-detail-name" className="mt-3 font-display text-3xl md:text-4xl text-[#16241B] tracking-tight">
            {product.name}
          </h1>

          <div className="mt-5 flex items-baseline gap-4">
            <p className="font-display font-bold text-3xl text-[#0E7A3B]">{formatXOF(product.price)}</p>
            <span className={`font-utility text-[10px] uppercase tracking-widest ${out ? "text-[#E8112D]" : "text-[#0E7A3B]"}`}>
              {out ? "Rupture de stock" : `${stock} en stock`}
            </span>
          </div>

          {product.description && (
            <p className="mt-6 text-[#16241B]/75 whitespace-pre-line leading-relaxed">{product.description}</p>
          )}

          {product.sizes?.length > 0 && (
            <div className="mt-6">
              <p className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60 mb-2">Taille</p>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => {
                  const label = s.size;
                  const disabled = s.stock !== null && s.stock <= 0;
                  return (
                    <button
                      key={label}
                      onClick={() => !disabled && setSize(label)}
                      disabled={disabled}
                      className={`px-4 py-2 rounded-full border text-sm transition-colors ${
                        size === label
                          ? "bg-[#0E7A3B] text-white border-[#0E7A3B]"
                          : "bg-white border-[#EDE7D9] hover:border-[#0E7A3B]"
                      } ${disabled ? "opacity-40 line-through cursor-not-allowed" : ""}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-6">
            <p className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60 mb-2">Quantité</p>
            <div className="inline-flex items-center rounded-full border border-[#EDE7D9] bg-white">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-2 hover:bg-[#FBFAF6]">−</button>
              <span data-testid="product-qty" className="px-6 font-display font-semibold">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(stock || 99, q + 1))} className="px-4 py-2 hover:bg-[#FBFAF6]">+</button>
            </div>
          </div>

          <button
            data-testid="product-add-to-cart"
            onClick={handleAdd}
            disabled={out || busy}
            className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-full bg-[#F5A800] text-[#16241B] px-8 py-4 text-base font-display font-bold shadow-[0_15px_40px_rgba(245,168,0,0.35)] hover:bg-[#0E7A3B] hover:text-white hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShoppingCart className="h-5 w-5" />}
            {out ? "Indisponible" : "Ajouter au panier"}
          </button>

          <div className="mt-8 grid grid-cols-2 gap-4">
            <div className="rounded-2xl border border-[#EDE7D9] bg-white p-4">
              <ShieldCheck className="h-5 w-5 text-[#0E7A3B]" />
              <p className="mt-2 text-xs font-medium text-[#16241B]">Paiement sécurisé</p>
              <p className="text-xs text-[#16241B]/60">Mobile Money & carte</p>
            </div>
            <div className="rounded-2xl border border-[#EDE7D9] bg-white p-4">
              <Truck className="h-5 w-5 text-[#F5A800]" />
              <p className="mt-2 text-xs font-medium text-[#16241B]">Livraison rapide</p>
              <p className="text-xs text-[#16241B]/60">Bénin — suivi GPS en direct</p>
            </div>
          </div>

          <div className="mt-8">
            <AppPromoCard compact />
          </div>
        </div>
      </div>
    </section>
  );
}
